// =============================================================================
// ProviderCredentialsTab — admin-managed EXTERNAL_API integration secrets.
// Secrets are write-only: the API returns a masked status ({ configured,
// last4, source }) and never echoes plaintext. Leaving a secret field blank
// keeps the currently stored value.
// =============================================================================

import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SecretStatus {
  configured: boolean;
  last4: string;
  source: 'database' | 'environment' | 'unset';
}

interface CredentialsStatus {
  apiEndpoint: string | null;
  apiEndpointSource: 'database' | 'environment' | 'unset';
  bearerToken: SecretStatus;
  webhookSecret: SecretStatus;
}

interface TestResult {
  ok: boolean;
  httpStatus: number | null;
  latencyMs: number;
  bearerConfigured: boolean;
  error?: string;
}

interface ProviderCredentialsTabProps {
  providerId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sourceBadge(status: SecretStatus | { source: string; configured?: boolean }) {
  switch (status.source) {
    case 'database':
      return <Badge variant="success" size="sm">Set in admin</Badge>;
    case 'environment':
      return <Badge variant="info" size="sm">From environment</Badge>;
    default:
      return <Badge variant="warning" size="sm">Not configured</Badge>;
  }
}

function secretPlaceholder(status: SecretStatus): string {
  if (!status.configured) return 'Enter a value to configure';
  return `Configured (••••${status.last4}) — leave blank to keep`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderCredentialsTab({ providerId }: ProviderCredentialsTabProps) {
  const queryClient = useQueryClient();

  const [apiEndpoint, setApiEndpoint] = useState<string | null>(null);
  const [bearerToken, setBearerToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const statusQuery = useQuery<{ data: CredentialsStatus }, Error>({
    queryKey: ['admin', 'provider', providerId, 'credentials'],
    queryFn: () =>
      api.get<{ data: CredentialsStatus }>(
        `/api/v1/admin/providers/${providerId}/credentials`,
      ),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api.patch<{ data: CredentialsStatus }>(
        `/api/v1/admin/providers/${providerId}/credentials`,
        body,
      ),
    onSuccess: () => {
      setFeedback({ type: 'success', text: 'Credentials saved.' });
      setBearerToken('');
      setWebhookSecret('');
      setApiEndpoint(null);
      setTestResult(null);
      queryClient.invalidateQueries({
        queryKey: ['admin', 'provider', providerId, 'credentials'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'provider', providerId] });
    },
    onError: (err: Error) =>
      setFeedback({ type: 'error', text: err.message || 'Failed to save credentials.' }),
  });

  const testMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: TestResult }>(
        `/api/v1/admin/providers/${providerId}/credentials/test`,
      ),
    onSuccess: (res) => {
      setFeedback(null);
      setTestResult(res.data);
    },
    onError: (err: Error) =>
      setFeedback({ type: 'error', text: err.message || 'Connection test failed.' }),
  });

  if (statusQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" label="Loading credentials..." />
      </div>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <p className="py-8 text-center text-sm text-danger">
        {statusQuery.error?.message ?? 'Failed to load credentials status.'}
      </p>
    );
  }

  const status = statusQuery.data.data;
  const endpointValue = apiEndpoint ?? status.apiEndpoint ?? '';

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    const body: Record<string, string> = {};
    if (apiEndpoint !== null && apiEndpoint.trim() !== (status.apiEndpoint ?? '')) {
      body.apiEndpoint = apiEndpoint.trim();
    }
    if (bearerToken.trim()) {
      if (bearerToken.trim().length < 16) {
        setFeedback({ type: 'error', text: 'Bearer token must be at least 16 characters.' });
        return;
      }
      body.bearerToken = bearerToken.trim();
    }
    if (webhookSecret.trim()) {
      if (webhookSecret.trim().length < 32) {
        setFeedback({ type: 'error', text: 'Webhook secret must be at least 32 characters.' });
        return;
      }
      body.webhookSecret = webhookSecret.trim();
    }

    if (Object.keys(body).length === 0) {
      setFeedback({ type: 'error', text: 'Nothing to save — enter at least one new value.' });
      return;
    }
    saveMutation.mutate(body);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">API credentials</h3>
        <p className="mt-1 text-xs text-text-secondary">
          Used for outbound order submission (bearer token) and HMAC signing /
          callback verification (webhook secret). Values are encrypted at rest
          and never displayed again after saving. Environment variables act as
          a fallback until values are set here.
        </p>
      </div>

      {feedback && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            feedback.type === 'success'
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-danger/30 bg-danger/10 text-danger'
          }`}
        >
          {feedback.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-text-primary">API endpoint</label>
            {sourceBadge({ source: status.apiEndpointSource })}
          </div>
          <Input
            value={endpointValue}
            onChange={(e) => setApiEndpoint(e.target.value)}
            placeholder="https://provider.example.com"
            autoComplete="off"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-text-primary">Bearer token</label>
            {sourceBadge(status.bearerToken)}
          </div>
          <Input
            type="password"
            value={bearerToken}
            onChange={(e) => setBearerToken(e.target.value)}
            placeholder={secretPlaceholder(status.bearerToken)}
            autoComplete="new-password"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-text-primary">Webhook secret</label>
            {sourceBadge(status.webhookSecret)}
          </div>
          <Input
            type="password"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder={secretPlaceholder(status.webhookSecret)}
            autoComplete="new-password"
          />
          <p className="text-[11px] text-text-tertiary">
            Minimum 32 characters. Rotating this immediately affects inbound
            callback verification — coordinate the change with the provider.
          </p>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button type="submit" variant="primary" size="sm" loading={saveMutation.isPending}>
            Save credentials
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={testMutation.isPending}
            onClick={() => testMutation.mutate()}
          >
            Test connection
          </Button>
        </div>
      </form>

      {testResult && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            testResult.ok
              ? 'border-success/30 bg-success/10'
              : 'border-danger/30 bg-danger/10'
          }`}
        >
          {testResult.ok ? (
            <p className="text-success">
              Endpoint reachable — HTTP {testResult.httpStatus} in {testResult.latencyMs}ms
              {!testResult.bearerConfigured && ' (no bearer token configured)'}
            </p>
          ) : (
            <p className="text-danger">
              Connection failed after {testResult.latencyMs}ms
              {testResult.error ? ` — ${testResult.error}` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
